import { FC, ReactNode, useMemo } from "react";

import FlexContainer from "@/components/FlexContainer";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";

import { ModalInfoType } from "../..";
import { FinGroups } from "./components/FinGroups";
import { ProgressiveScale } from "./components/ProgressiveScale";
import { SequentialScale } from "./components/SequentialScale";
import { StyledDIModal } from "./Styled";

interface DashboardInfoModalProps {
    modalKey: string;
    isOpen: boolean;
    onClose: () => void;
    groupName?: string;
    modalType?: ModalInfoType["type"];
    details?: ModalInfoType["details"];
}

const modalContentMap: Record<
    ModalInfoType["type"],
    (details: ModalInfoType["details"]) => ReactNode
> = {
    fin_groups: (details) => (
        <FinGroups
            finGroups={(details as { finGroups: string[] })?.finGroups ?? []}
        />
    ),
    progressive_scale: (details) => {
        const { currentPercent, progressive } = details as {
            currentPercent: number;
            progressive: { from: number; coefficient: number }[];
        };
        return (
            <ProgressiveScale
                currentPercent={currentPercent}
                progressive={progressive}
            />
        );
    },
    sequential_scale: (details) => {
        const { minComplete, maxComplete } = details as {
            minComplete: number;
            maxComplete: number;
        };
        return (
            <SequentialScale
                minComplete={minComplete}
                maxComplete={maxComplete}
            />
        );
    },
};

export const DashboardInfoModal: FC<DashboardInfoModalProps> = ({
    modalKey,
    isOpen,
    onClose,
    groupName,
    modalType,
    details,
}) => {
    const content = useMemo(() => {
        if (!modalType || !details) return null;
        const renderContent = modalContentMap[modalType];
        return renderContent ? renderContent(details) : null;
    }, [modalType, details]);

    return (
        <StyledDIModal
            key={modalKey}
            open={isOpen}
            onOk={onClose}
            onCancel={onClose}
            centered
            footer={null}
            destroyOnHidden
        >
            <FlexContainer width="100%" style={{ padding: "24px" }}>
                <PrimaryText
                    fontSize={20}
                    fontWeight="500"
                    style={{ textAlign: "center" }}
                >
                    {groupName ?? "Інформація"}
                </PrimaryText>
            </FlexContainer>

            {content ?? <SecondaryText>Дані відсутні</SecondaryText>}
        </StyledDIModal>
    );
};
